'use client';

import {
  Stack,
  Box,
  Typography,
  IconButton,
  MenuItem,
  Select,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useAuthContext } from 'src/auth/hooks';
import Iconify from 'src/components/iconify';
import Label from 'src/components/label';
import { usePathname, useRouter } from 'src/routes/hooks';
import { archiveJob, fetchJobOpenings, pinJob, unpinJob, getPinnedJobs } from 'src/services/role.service';
import { getOrganizationDetails } from 'src/services/user.service';
import { getOrganizationId } from 'src/services/user.service';
import { paths } from 'src/routes/paths';
import { debounce } from 'lodash';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { useSnackbar } from 'notistack';
import { LoadingScreen } from 'src/components/loading-screen';
import fDateDiff from 'src/utils/format-time';
import { useErrorBoundary } from 'react-error-boundary';
import { useQuery } from 'src/hooks/use-query';
import Image from 'src/components/image';
import { useNavigate } from 'react-router-dom';
import { SearchBar } from '../components/search-bar';
import JobFilters from '../components/filters/job-filters';
import FilterResults from '../components/filters/job-filters/filter-results';
import ApprovalDialog from 'src/components/approval-popup/approval-popup';

const JOB_STATUS_MAP = {
  ACTIVE: {
    label: 'Live',
    value: 'ACTIVE',
  },
  DRAFT: {
    label: 'Draft',
    value: 'DRAFT',
  },
  ARCHIVED: {
    label: 'Archived',
    value: 'ARCHIVED',
  },
  EXPIRED: {
    label: 'Expired',
    value: 'EXPIRED',
  },
  FAILED: {
    label: 'Failed',
    value: 'FAILED',
  },

  LIMITED: {
    label: 'Limited',
    value: 'LIMITED',
  },
};

export default function JobListView() {
  // hooks
  const { user, verified } = useAuthContext();
  const popover = usePopover();
  const { enqueueSnackbar } = useSnackbar();
  const queryParams = useQuery();
  const navigate = useNavigate();

  console.log("Is user Verified ?"+verified);

  const selectedJobStatus = queryParams.get('status');

  // variables
  const organization_id = getOrganizationId(user);

  // states
  const [isLoading, setIsLoading] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);
  const [isUserVerified, setIsUserVerified] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [selectedJobPopover, setSelectedJobPopover] = useState(null);
  const [showJobFilters, setShowJobFilters] = useState(false);
  const [pinnedJobIds, setPinnedJobIds] = useState(new Set());
  const [jobsCount, setJobsCount] = useState({
    ACTIVE: 0,
    ARCHIVED: 0,
    DRAFT: 0,
    EXPIRED: 0,
    FAILED: 0,
    LIMITED:0,
  });


  // router
  const pathname = usePathname();
  const router = useRouter();

  // functions
  const searchJob = debounce(searchText => {
    const lowerCaseSearchText = searchText.toLowerCase().trim();

    const _jobs = jobs.filter(job =>
      job.name.toLowerCase().includes(lowerCaseSearchText),
    );

    setFilteredJobs(_jobs);
  }, 200);

  const copyLink = async link => {
    const jobApplyLink = `${process.env.REACT_APP_APPLY_JOB_BASE_URL}${link}`;
    await navigator.clipboard.writeText(jobApplyLink);
    enqueueSnackbar('Copied');
  };

  const { showBoundary } = useErrorBoundary();

  const getOrganizationJobs = useCallback(async () => {
    try {
      setIsLoading(true);

      const payload = {};

      if (selectedJobStatus) payload.status = selectedJobStatus;

      const data = await fetchJobOpenings(organization_id, payload);

      if (data !== undefined && data !== null && data.success) {
        const _jobs = data.body.job_openings
          .map((job_opening, index) => ({
            name: job_opening.name,
            candidatesCount: job_opening.candidate_counts,
            createdAt: job_opening.created_at,
            id: job_opening.job_opening_id,
            status: job_opening.status,
            jobApplyLink: job_opening.job_apply_link,
            screeningLink: job_opening.screening_link,
          }))
          .sort((a, b) => {
            const aDate = new Date(a.createdAt);
            const bDate = new Date(b.createdAt);
            return bDate - aDate;
          });
        const _jobsCount = data.body.job_opening_status_map;
        setJobsCount(_jobsCount);
        setJobs(_jobs);
        setFilteredJobs(_jobs);
      } else throw new Error(data.message);
    } catch (err) {
      console.log(err);
      showBoundary(err);
    } finally {
      setIsLoading(false);
    }
  }, [organization_id, selectedJobStatus, showBoundary]);

  const fetchPinnedJobs = useCallback(async () => {
    try {
      const data = await getPinnedJobs(organization_id);
      if (data && data.success) {
        const ids = new Set(data.body.map(pin => pin.job_id));
        setPinnedJobIds(ids);
      }
    } catch (err) {
      console.log(err);
    }
  }, [organization_id]);

  const handleTogglePin = async (event, job) => {
    event.stopPropagation();
    try {
      if (pinnedJobIds.has(job.id)) {
        await unpinJob(organization_id, job.id);
        setPinnedJobIds(prev => {
          const next = new Set(prev);
          next.delete(job.id);
          return next;
        });
        enqueueSnackbar('Job unpinned');
      } else {
        await pinJob(organization_id, job.id);
        setPinnedJobIds(prev => new Set(prev).add(job.id));
        enqueueSnackbar('Job pinned');
      }
    } catch (err) {
      console.log(err);
      enqueueSnackbar('Something went wrong', { variant: 'error' });
    }
  };

  const fetchOrganizationDetails = useCallback(
    async () => {
      try {
        const response = await getOrganizationDetails(organization_id);
        if (response.success) {
          const orgDetails = response.body;
          const isOrgApproved=orgDetails.approval_status;
          if(isOrgApproved && isOrgApproved != null){
            if(isOrgApproved === "PENDING_APPROVAL"){
              console.log(isOrgApproved);
              setIsUserVerified(false);
            }else{
              setIsUserVerified(true);
              console.log(isOrgApproved);
            }
          }else{
            setIsUserVerified(true);
          }          
        } else throw new Error(response.message);
      } catch (err) {
        console.log(err);
      }
    },
    [organization_id],
  );


  // effects
  useEffect(() => {
    fetchOrganizationDetails();
    // setIsUserVerified(verified);
    getOrganizationJobs();
    fetchPinnedJobs();
  }, [getOrganizationJobs, organization_id, fetchOrganizationDetails, fetchPinnedJobs]);

  useEffect(() => {
    if (!isLoading) {
      setIsEmpty(filteredJobs.length === 0);
    }
  }, [filteredJobs.length, isLoading]);

  const StatusDropdown = (
    <Select
      value={selectedJobStatus}
      onChange={e => {
        router.replace(`${pathname}?status=${e.target.value}`);
      }}
      sx={{
        width: '130px',
        '& .MuiOutlinedInput-input': {
          minWidth: 'auto',
        },
      }}
    >
      {Object.keys(JOB_STATUS_MAP).map(key => (
        <MenuItem
          key={JOB_STATUS_MAP[key].value}
          value={JOB_STATUS_MAP[key].value}
        >
          {JOB_STATUS_MAP[key].label} {jobsCount[JOB_STATUS_MAP[key].value]}
        </MenuItem>
      ))}
    </Select>
  );

  const FilterButton = (
    <IconButton
      variant="outlined"
      sx={{
        borderColor: theme => selectedJobStatus && theme.palette.info.main,
      }}
      onClick={() => setShowJobFilters(true)}
    >
      <Iconify icon="tabler:adjustments-horizontal" />
    </IconButton>
  );

  const SortButton = (
    <IconButton variant="outlined">
      <Iconify icon="tabler:arrows-sort" />
    </IconButton>
  );


  if (isLoading) return <LoadingScreen />;

  return (
    <>
    {isUserVerified === false ? 
      <ApprovalDialog/>
    :
    <Stack pl="60px" pr="80px" py="40px" minHeight="100vh">
      <Stack direction="row" alignItems="center" mb="20px">
        <Box flexGrow={1} />
        <Stack direction="row" alignItems="center" spacing="12px">
          <SearchBar onChange={e => searchJob(e.target.value)} />
          {/* {StatusDropdown} */}
          {FilterButton}
          {/* {SortButton} */}
          <JobFilters
            open={showJobFilters}
            setOpen={setShowJobFilters}
            selectedJobStatus={selectedJobStatus}
            jobsCount={jobsCount}
            JOB_STATUS_MAP={JOB_STATUS_MAP}
          />
        </Stack>
      </Stack>
      <Stack mb="20px">
        {selectedJobStatus && (
          <FilterResults filterResultsCount={jobsCount[selectedJobStatus]} />
        )}
      </Stack>
      <Stack spacing="16px" height="100%">
        {!isEmpty &&
          [...filteredJobs]
            .sort((a, b) => {
              const aPinned = pinnedJobIds.has(a.id) ? 0 : 1;
              const bPinned = pinnedJobIds.has(b.id) ? 0 : 1;
              return aPinned - bPinned;
            })
            .map((job, index) => (
            <Box
              key={`job-${index}`}
              sx={{
                borderRadius: '6px',
                border: theme =>
                  `1px solid ${theme.palette.border.primaryBlack3}`,
                bgcolor: theme => theme.palette.background.primaryWhite1,
                p: '16px',
                cursor: 'pointer',
              }}
              onClick={() => {
                if (job.status === 'DRAFT') {
                  router.push(paths.dashboard.jobs.edit(job.id));
                } else {
                  navigate(paths.dashboard.jobs.details(job.id), {
                    state: {
                      jobId: job.id,
                      jobName: job.name,
                      candidatesCount: job.candidatesCount,
                      jobStatus: job.status,
                      publicApplyLink: job.jobApplyLink,
                      screeningLink: job.screeningLink,
                    },
                  });
                }
              }}
              minHeight="142px"
            >
              <Stack spacing="50px">
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="h4">{job.name}</Typography>
                  <Stack direction="row" alignItems="center" spacing="12px">
                    <Typography
                      variant="caption"
                      color={theme => theme.palette.content.primaryBlack3}
                    >
                      {fDateDiff(job.createdAt)}
                    </Typography>
                    <Stack direction="row" spacing="6px">
                      <Typography
                        color={theme =>
                          job.status === 'ACTIVE'
                            ? theme.palette.content.primaryGreen1
                            : theme.palette.content.primaryBlack3
                        }
                        variant="caption"
                      >
                        {JOB_STATUS_MAP[job.status].label}
                      </Typography>
                    </Stack>
                    <IconButton
                      onClick={event => handleTogglePin(event, job)}
                      title={pinnedJobIds.has(job.id) ? 'Unpin job' : 'Pin job'}
                    >
                      <Iconify
                        icon={pinnedJobIds.has(job.id) ? 'tabler:pin-filled' : 'tabler:pin'}
                        sx={{
                          color: theme =>
                            pinnedJobIds.has(job.id)
                              ? theme.palette.common.black
                              : theme.palette.content.primaryBlack3,
                        }}
                      />
                    </IconButton>
                    <IconButton
                      onClick={event => {
                        setSelectedJobPopover(job);

                        popover.onOpen(event);
                        event.stopPropagation();
                      }}
                    >
                      <Iconify icon="tabler:dots-vertical" />
                    </IconButton>
                  </Stack>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Stack direction="row" spacing="10px">
                    {!job.candidatesCount.APPLIED && job.jobApplyLink && (
                      <Box
                        sx={{
                          padding: '8px 12px',
                          borderRadius: '8px',
                          bgcolor: theme =>
                            theme.palette.background.primaryGrey2,
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing="8px"
                        >
                          <Typography variant="input" fontWeight={500}>
                            Share the job apply link.
                          </Typography>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing="2px"
                            onClick={event => {
                              copyLink(job.jobApplyLink);
                              event.stopPropagation();
                            }}
                          >
                            <Typography
                              variant="input"
                              fontWeight={500}
                              color={theme => theme.palette.content.accent1}
                            >
                              Copy link
                            </Typography>
                            <Iconify
                              sx={{
                                color: theme => theme.palette.content.accent1,
                              }}
                              icon="tabler:copy"
                            />
                          </Stack>
                        </Stack>
                      </Box>
                    )}
                    {job.candidatesCount.APPLIED ? (
                      <Label
                        sx={{ color: theme => theme.palette.common.black }}
                        color="warning"
                      >
                        Applied {job.candidatesCount.APPLIED}
                      </Label>
                    ) : null}
                    {job.candidatesCount.AI_INTERVIEW ? (
                      <Label
                        color="info"
                        sx={{
                          color: theme => theme.palette.content.primaryBlack1,
                        }}
                      >
                        AI Interview {job.candidatesCount.AI_INTERVIEW}
                      </Label>
                    ) : null}
                    {job.candidatesCount.IN_PERSON_INTERVIEW ? (
                      <Label
                        sx={{
                          color: 'content.primaryBlack1',
                          bgcolor: 'background.primaryBlue1',
                        }}
                      >
                        Interview {job.candidatesCount.IN_PERSON_INTERVIEW}
                      </Label>
                    ) : null}
                    {job.candidatesCount.HIRED ? (
                      <Label
                        sx={{
                          background: theme =>
                            theme.palette.background.primaryGreen2,
                        }}
                      >
                        Hired {job.candidatesCount.HIRED}
                      </Label>
                    ) : null}
                  </Stack>
                  <Stack direction="row">
                    {job.candidatesCount.NEED_REVIEW ? (
                      <Label
                        sx={{
                          background: theme =>
                            theme.palette.background.primaryRed2,
                        }}
                        startIcon={<Iconify icon="tabler:alert-triangle" />}
                      >
                        {job.candidatesCount.NEED_REVIEW} Needs review
                      </Label>
                    ) : null}
                  </Stack>
                </Stack>
              </Stack>
            </Box>
          ))}

        {isEmpty && (
          <Stack m="auto">
            <Image src="/assets/illustrations/man-on-bicycle.svg" />
          </Stack>
        )}

        {selectedJobPopover && (
          <CustomPopover
            open={popover.open}
            onClose={() => {
              popover.onClose();
              setSelectedJobPopover(null);
            }}
            arrow="right-top"
            sx={{ width: 160 }}
          >
            {selectedJobPopover.jobApplyLink && (
              <MenuItem
                onClick={() => {
                  copyLink(selectedJobPopover.jobApplyLink);
                  popover.onClose();
                }}
              >
                Copy Apply Link
              </MenuItem>
            )}
            {selectedJobPopover.screeningLink && (
              <MenuItem
                onClick={() => {
                  copyLink(selectedJobPopover.screeningLink);
                  popover.onClose();
                }}
              >
                Copy Screening Link
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                router.push(paths.dashboard.jobs.edit(selectedJobPopover.id));
                popover.onClose();
              }}
            >
              Edit Job Details
            </MenuItem>
            {/* <MenuItem
              onClick={() => {
                popover.onClose();
              }}
            >
              Job application
            </MenuItem> */}
            {user && user.user_role === 'ADMIN' && (
              <MenuItem
                onClick={async () => {
                  await archiveJob(organization_id, selectedJobPopover.id);
                  enqueueSnackbar('Job archived');
                  await getOrganizationJobs();
                  popover.onClose();
                }}
              >
                Archive
              </MenuItem>
            )}
          </CustomPopover>
        )}
      </Stack>
    </Stack>
    }
    </>
  );
}
